import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Timeout } from 'ahooks/lib/useRequest/src/types';
import type { ECharts } from 'echarts';
import type { ParamsTimeType } from '@op/types/common';
import { useStateRef } from '@op/hooks/common';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { getEventXAxisAxisLabel } from '../hooks/utils/buildDualEventXAxis';
import { getEventAxisLabelAnchor } from '../hooks/utils/getEventAxisLabelAnchor';
import { useOpinionDateAxisPointerSync } from '../hooks/useOpinionDateAxisPointerSync';
import { shouldDeferOpinionChartAxisPointerClear } from '../hooks/utils/syncOpinionChartDateAxisPointer';
import {
  useOpinionEventFloaterPosition,
  type IOpinionEventFloaterAnchor,
} from '../hooks/useOpinionEventFloaterPosition';
import type { IOpinionChartEventData } from '../hooks/types';
import OpinionEventFloater, {
  type IOpinionEventFloaterDetail,
} from '../OpinionEventFloater';

type IAxisEventDetailState = IOpinionEventFloaterDetail & {
  time: string;
};

interface IOpinionEventDetailsPopProps {
  myChart: ECharts | null;
  eventList: IOpinionChartEventData['chartRenderEventList'];
  dateType: ParamsTimeType;
  xAxisData: string[];
  showEventAxis: boolean;
}

export default function OpinionEventDetailsPop({
  myChart,
  eventList,
  dateType,
  xAxisData,
  showEventAxis,
}: IOpinionEventDetailsPopProps) {
  const hideTimer = useRef<Timeout | null>(null);
  const isMouseOverPopup = useRef(false);
  const axisPointerRef = useRef<{
    value: string;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const [showEventListRef, showEventList, setShowEventList] =
    useStateRef<IAxisEventDetailState | null>(null);
  const { scheduleClear, clear, setSuppressAxisPointer } =
    useOpinionDateAxisPointerSync(myChart, xAxisData, showEventAxis);

  const hide = useCallback(() => {
    setShowEventList(null);
    axisPointerRef.current = null;
    setSuppressAxisPointer(false);
    clear();
    myChart?.setOption({
      xAxis: [{ axisLabel: getEventXAxisAxisLabel(eventList) }],
    });
  }, [clear, eventList, myChart, setShowEventList, setSuppressAxisPointer]);

  useEffect(() => {
    if (!myChart) {
      return undefined;
    }

    let isOnAxis = false;

    const handleMouseMove = (params: any) => {
      isOnAxis = true;
      if (params.targetType !== 'axisLabel') {
        return;
      }

      const { event, value } = params || {};
      if (showEventListRef.current?.time === value) {
        return;
      }

      buriedBtnPoint('KMLG_Eventaxis');
      myChart.setOption({
        xAxis: [{ axisLabel: getEventXAxisAxisLabel(eventList, value) }],
      });

      const axisEventList = eventList?.[value];
      if (!axisEventList?.event_list) {
        return;
      }

      setSuppressAxisPointer(true);
      clear();
      axisPointerRef.current = {
        value,
        offsetX: event?.offsetX ?? 0,
        offsetY: event?.offsetY ?? 0,
      };
      setShowEventList({ ...axisEventList, time: value });
    };

    const handleMouseOut = () => {
      if (hideTimer.current !== null) {
        clearTimeout(hideTimer.current);
      }
      hideTimer.current = setTimeout(() => {
        if (isMouseOverPopup.current || isOnAxis) {
          return;
        }
        if (shouldDeferOpinionChartAxisPointerClear(myChart)) {
          return;
        }
        scheduleClear();
        hide();
      }, 50);
      isOnAxis = false;
    };

    myChart.on('mousemove', { id: 'eventDetails' }, handleMouseMove);
    myChart.on('mouseout', { id: 'eventDetails' }, handleMouseOut);
    myChart.on('globalout', { id: 'eventDetails' }, handleMouseOut);

    return () => {
      myChart.off('mousemove', handleMouseMove);
      myChart.off('mouseout', handleMouseOut);
      myChart.off('globalout', handleMouseOut);
    };
  }, [
    eventList,
    hide,
    myChart,
    scheduleClear,
    setShowEventList,
    showEventListRef,
    setSuppressAxisPointer,
    clear,
  ]);

  const getAnchor = useCallback((): IOpinionEventFloaterAnchor | null => {
    if (!myChart || !axisPointerRef.current?.value) {
      return null;
    }
    const { value, offsetX, offsetY } = axisPointerRef.current;
    return getEventAxisLabelAnchor(myChart, value, xAxisData, {
      offsetX,
      offsetY,
    });
  }, [myChart, xAxisData]);

  const { time, ...eventDetail } = showEventList || {};
  const floaterStyle = useOpinionEventFloaterPosition(!!time, getAnchor, 'above');

  if (!time || !floaterStyle) {
    return null;
  }

  return createPortal(
    <OpinionEventFloater
      time={time}
      eventDetail={eventDetail}
      dateType={dateType}
      style={floaterStyle}
      onMouseEnter={() => {
        isMouseOverPopup.current = true;
        setSuppressAxisPointer(true);
      }}
      onMouseLeave={() => {
        isMouseOverPopup.current = false;
        hide();
      }}
    />,
    document.body,
  );
}
